// ─────────────────────────────────────────────────────────────────────────────
// Sesión Frontline (RF-002) — usuarios SIN Azure AD (sin correo corporativo).
//
// Estos usuarios no existen como identidad de sign-in en Entra ID, así que no pueden usar MSAL/Teams
// SSO. El UserAdminService emite un JWT PROPIO al hacer login por contraseña (POST /v1/users/
// frontline-login); aquí se persiste en localStorage y `acquireToken`/`client.ts` lo prefieren sobre
// el token de Azure AD cuando existe una sesión Frontline. Paralelo a MSAL, no lo reemplaza.
// ─────────────────────────────────────────────────────────────────────────────

import { withBase } from '@/utils/basePath';

const TOKEN_KEY = 'pmp.frontline.token';
const USER_KEY = 'pmp.frontline.user';

export interface FrontlineSessionUser {
  id: string;
  email: string;
  displayName?: string | null;
  ouId?: string | null;
  preferredLanguage?: string | null;
}

export function saveFrontlineSession(token: string, user: FrontlineSessionUser): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch {
    /* storage no disponible (modo privado): la sesión durará solo en memoria del tab actual */
  }
}

export function getFrontlineToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function getFrontlineUser(): FrontlineSessionUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as FrontlineSessionUser) : null;
  } catch {
    return null;
  }
}

export function clearFrontlineSession(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch {
    /* no-op */
  }
}

export function hasFrontlineSession(): boolean {
  return Boolean(getFrontlineToken());
}

/**
 * Cierra la sesión Frontline y manda al login. Los colaboradores Frontline NO están en Azure AD, así
 * que ante un token vencido/inválido no pueden caer a MSAL — la única recuperación es re-loguearse.
 * El guard de ruta evita un bucle de redirección si ya estamos en el login.
 *
 * Vive aquí (y no en un cliente HTTP) porque los DOS clientes del repo la necesitan y el ciclo de
 * vida de la sesión es de este módulo.
 */
export function forceFrontlineRelogin(): void {
  clearFrontlineSession();
  if (typeof window !== 'undefined' && !window.location.pathname.endsWith('/login')) {
    window.location.assign(withBase('/login'));
  }
}

/** `exp` (epoch en segundos) del payload de un JWT, o null si no es legible. */
function readJwtExp(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: number };
    return typeof json.exp === 'number' ? json.exp : null;
  } catch {
    return null;
  }
}

/**
 * true si el JWT Frontline guardado ya expiró (con 30s de margen de reloj). El cliente HTTP lo usa
 * para forzar re-login en vez de mandar un token muerto que solo generaría un 401 en bucle. Si el
 * token no trae `exp` legible, devuelve false (deja que el servidor decida con un 401 real).
 */
export function isFrontlineTokenExpired(): boolean {
  const token = getFrontlineToken();
  if (!token) return false;
  const exp = readJwtExp(token);
  if (exp === null) return false;
  return Date.now() >= (exp - 30) * 1000;
}
